import React, { useState, useRef, useEffect, useMemo } from "react";

import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import { nanoid } from "nanoid";

import Auth from "shared/@auth";
import { useDocuments } from "ManagementTeam/DocumentsPage/useDocuments";
import { useAgenda } from "ManagementTeam/AgendaPage/useAgenda";

import { markdownParse } from "ManagementTeam/utils/markdownParse";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import Button from "shared/Button";
import ObjectSelect from "../../Shared/ObjectSelect";
import Toggler from "../Toggler";
import MultipleChoiceItem from "../MultipleChoiceItem";
import TextEditor from "../TextEditor";
import Modal from "../Modal";
import ModalCard from "../ModalCard";
import ModalActionBar from "shared/ModalActionBar";
import CheckboxItem from "../CheckboxItem";
import Input from "shared/@forms/Input";
import { OptionsDropdown } from "../../OptionsDropdown/OptionsDropdown";

import { ReactComponent as CancelButtonImg } from "../../assets/Edit_Category_Cancel.svg";
import { ReactComponent as SaveButtonImg } from "../../assets/Edit_Category_Save.svg";
import { ReactComponent as SaveDraftButtonImg } from "../../assets/Edit_Category_Draft.svg";
import { ReactComponent as RemoveButtonImg } from "../../assets/Edit_Category_Remove.svg";
import CloseButton from "../../assets/Close_Button.svg";
import PDFIcon from "../ViewPdfButton/pdf_Icon.png";

import styles from "./AddNewModal.module.css";
import Markdown from "../Markdown";

/**
 * Interactive component. Modal when adding an agenda item on the Agenda page.
 */
export const AddNewModal = React.memo(function ({
  isOpen,
  onClose,
  isActionItem,
  onSave,
  onSaveDraft,
  itemInfo,
  meetingDates = [],
  itemOwners,
  currentUser,
  users,
  onDeleteDocument,
  isDecisionDefault = false,
  defaultDate = null,
}) {
  const id = nanoid();
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const EDocumentOptionType = {
    AddNew: -1,
    RemoveFromAgenda: -2,
    RemoveFromAgendaAndDoc: -3,
  };

  const { hasPermission } = Auth.useContainer();

  const { agendaItemPublishPut } = useAgenda();
  const { unassignedDocuments, docDelete } = useDocuments();

  const noteRef = useRef(null);
  const fileRef = useRef(null);

  //For validating field
  const [errors, setErrors] = useState({ errorItemOwner: "" });

  //Agenda Data
  const [tempAgenda, setTempAgenda] = useState(itemInfo);
  //Meeting Date
  const [useMeetingItem, setUseMeetingItem] = useState(null);

  //Item Owner
  const [_itemOwners, setItemOwners] = useState([]);
  const [currentItemOwner, setCurrentItemOwner] = useState(null);

  //Description || Notes
  const [tempDescription, setTempDescription] = useState(
    itemInfo.description || ""
  );
  const [tempNote, setTempNote] = useState("");

  //Voting Requirements
  const [useSelections, setUseSelections] = useState(null);
  const [tempSelections, setTempSelections] = useState(
    itemInfo.selections.map((selection, index) => {
      return { id: timeId() + index, selection: selection, deleted: false };
    })
  );

  //Documents
  const [documentOptions, setDocumentOptions] = useState([]); //Document Options: Remove | Unassign Docs | Add New
  const [selectedDocument, setSelectedDocument] = useState(itemInfo.document); //UI List document
  const [document, setDocument] = useState(itemInfo.document); //UI List document
  const [newDocumentFile, setNewDocumentFile] = useState(null); //UI New File
  const [isDocumentDownloadable_Nullable, setIsDocumentDownloadable_Nullable] =
    useState(null);
  const [requestDeleteDocuments, setRequestDeleteDocuments] = useState([]); //Mark document is removed from agenda & documents
  const titleInput = useRef();
  
  useEffect(() => {
    const isDownloadable =
      itemInfo && itemInfo.document ? itemInfo.document.isDownloadable : null;
    setIsDocumentDownloadable_Nullable(isDownloadable);

    const interval = setInterval(() => {
      console.log("titleInput.current", titleInput.current)
      if (titleInput.current) {
        titleInput.current.focus();
        clearInterval(interval)
      }
    }, 500);
  }, []);

  useEffect(() => {
    //Reassign value for options when:
    //1. Reassign if unassignedDocuments changed
    //2. Reassign if selectedDocument changed when trigger handleClickDocumentOption

    let options = [];

    if (selectedDocument && !selectedDocument.isUnassigned) {
      const documentActionOpts = [
        {
          id: EDocumentOptionType.RemoveFromAgenda,
          option: "Remove Document from Agenda",
        },
        {
          id: EDocumentOptionType.RemoveFromAgendaAndDoc,
          option: "Remove Document from Agenda & Docs",
        },
      ];

      options = [...options, ...documentActionOpts];
    }

    if (document && document._id != selectedDocument._id) {
      options.push({
        id: document._id,
        option: document.files[0].name,
      });
    }

    if (unassignedDocuments && unassignedDocuments.length > 0) {
      const documentOpts = unassignedDocuments
        .filter((a) => a.files && a.files.length > 0)
        .map((a) => ({
          id: a._id,
          option: a.files[0].name,
        }));

      options.push(...documentOpts);
    }

    const otherActions = [
      {
        id: EDocumentOptionType.AddNew,
        option: "Add New",
      },
    ];

    options.push(...otherActions);

    setDocumentOptions([...options]);
  }, [unassignedDocuments, selectedDocument]);

  useMemo(() => {
    if (isDecisionDefault) {
      setTempAgenda({
        ...tempAgenda,
        addTo: [...tempAgenda.addTo, "Decisions (Voting)"],
        votingRequirements: ['"A Block" Vote'],
      });
    }
  }, [isDecisionDefault]);
  useMemo(() => {
    tempAgenda && setUseMeetingItem(tempAgenda);
  }, [tempAgenda]);

  useMemo(() => {
    itemOwners && itemOwners.length > 0 && setItemOwners(itemOwners);
  }, [itemOwners]);

  useMemo(() => {
    if (errors && errors.errorItemOwner && currentItemOwner) {
      setCurrentItemOwner({
        ...currentItemOwner,
        option:
          (currentItemOwner.option || "").replace(" - No Longer MT1", "") +
          " - No Longer MT1",
      });
      return;
    }

    if (_itemOwners && _itemOwners.length > 0) {
      if (itemInfo.itemOwner.name) {
        let ownerObject = _itemOwners.filter(
          (owner) =>
            owner.option.toUpperCase() === itemInfo.itemOwner.name.toUpperCase()
        );
        setCurrentItemOwner(ownerObject[0]);
      } else {
        setCurrentItemOwner(_itemOwners[1]);
      }
    } else {
      setCurrentItemOwner({
        ...itemInfo.itemOwner,
        id: itemInfo.itemOwner._id,
        option: itemInfo.itemOwner.name,
      });
    }
  }, [_itemOwners]);

  useMemo(() => {
    tempSelections && setUseSelections(tempSelections);
  }, [tempSelections]);

  const selectedOptionMeeting = useMemo(() => {
    if (meetingDates.length > 0 && tempAgenda.meetingId) {
      return meetingDates.find((a) => a.id === tempAgenda.meetingId);
    }
    if (defaultDate) {
      return defaultDate;
    }
    if (meetingDates.length > 0) {
      return meetingDates[0];
    }
    if (
      tempAgenda.meetingId &&
      tempAgenda.meetingId.dates &&
      tempAgenda.meetingId.dates.length > 0
    ) {
      let dateString = tempAgenda.meetingId.dates.map((date) =>
        moment.utc(date.startsOn).format("MMMM DD, YYYY")
      );

      return dateString[0];
    }

    if (tempAgenda.meetingDate) {
      return moment.utc(tempAgenda.meetingDate).format("MMMM DD, YYYY");
    }

    return null;
  }, [meetingDates, tempAgenda, defaultDate]);

  const selectedOptionItemOwner = useMemo(() => {
    if (currentItemOwner && _itemOwners && _itemOwners.length > 0) {
      return currentItemOwner.option;
    }

    if (_itemOwners && _itemOwners.length > 0) {
      return itemInfo.itemOwner.name + " - No Longer MT1";
    }

    return itemInfo.itemOwner.name;
  }, [currentItemOwner, _itemOwners, itemInfo]);

  const handlePublishAgenda = () => {
    agendaItemPublishPut
      .mutateAsync({
        agendaId: itemInfo.id,
        published: true,
      })
      .then(() => {
        onClose();
      });
  };

  const handleUnpublishAgenda = () => {
    agendaItemPublishPut
      .mutateAsync({
        agendaId: itemInfo.id,
        published: false,
      })
      .then(() => {
        onClose();
      });
  };

  const handleSave = (type) => {
    if (!currentItemOwner) {
      setErrors({ ...errors, errorItemOwner: "Error" });
      return;
    } else {
      setErrors({ ...errors, errorItemOwner: "" });
    }

    //Skip validate when isActionItem
    if (
      !isActionItem &&
      itemOwners &&
      itemOwners.find((a) => a.id == currentItemOwner.id) == null
    ) {
      setErrors({ ...errors, errorItemOwner: "Error" });
      setItemOwners(itemOwners);
      return;
    }

    const formData = {
      ...useMeetingItem,
      itemOwner: currentItemOwner,
      description: tempDescription,
      selections: useMeetingItem.votingRequirements.includes("Multiple Choice")
        ? useSelections
            .filter((item) => !item.deleted)
            .map((object) => object.selection)
        : [],

      isDocumentDownloadable: isDocumentDownloadable_Nullable,
    };

    if (newDocumentFile) {
      formData.documents = [newDocumentFile];
      formData.documentId = null;
      formData.isUploadDocument = true;
    } else {
      formData.documentId = selectedDocument ? selectedDocument._id : null;
      formData.documents = selectedDocument ? selectedDocument.files : [];
      formData.isUploadDocument = false;
    }

    type === "save" ? onSave(formData) : onSaveDraft(formData);

    if (requestDeleteDocuments && requestDeleteDocuments.length > 0) {
      const last = requestDeleteDocuments[requestDeleteDocuments.length - 1];

      if (last.id)
        docDelete.mutateAsync(last.id).catch(() => {
          //Ignored or console log
        });
    }
  };

  const handleChangeItemOwnerSelected = (e) => {
    var index = 0;
    _itemOwners.map((owner, i) => {
      if (owner.id === e) {
        index = i;
      }
      return null;
    });
    setCurrentItemOwner(_itemOwners[index]);
  };

  const handleClickDocumentOption = (optionId) => {
    if (optionId === EDocumentOptionType.AddNew) {
      //Add New File - Show UI new file
      fileRef.current.click();
      return;
    }

    if (optionId === EDocumentOptionType.RemoveFromAgenda) {
      //Remove from agenda
      setSelectedDocument(null);
      setDocument(null);
      return;
    }

    if (optionId === EDocumentOptionType.RemoveFromAgendaAndDoc) {
      //Remove from agenda & document
      setRequestDeleteDocuments([...requestDeleteDocuments, selectedDocument]);
      setSelectedDocument(null);
      setDocument(null);
      return;
    }

    if (document && optionId == document.id) {
      //Re-selected agenda document
      setSelectedDocument(document);
      return;
    }

    //Select available document
    const selectedAvailableDoc = unassignedDocuments.find(
      (a) => a._id === optionId
    );
    if (isDocumentDownloadable_Nullable == null)
      setIsDocumentDownloadable_Nullable(true);

    setSelectedDocument({ ...selectedAvailableDoc, isUnassigned: true });
  };

  const handleNewDocumentFileChange = (e) => {
    let selectedFile = e.target.files;
    const fileName = fileRef.current.value.split(".");

    if (fileName[fileName.length - 1] !== "pdf") {
      alert("File Format not PDF");
      return;
    }

    var file = null;
    let fileToLoad = selectedFile[0];
    let fileReader = new FileReader();

    fileReader.onload = function (fileLoadedEvent) {
      file = fileLoadedEvent.target.result;

      const newFile = {
        name: fileName[0].replace(/^.*[\\/]/, ""),
        data: file,
        mimetype: "pdf",
        type: "pdf",
      };

      setIsDocumentDownloadable_Nullable(true);
      setNewDocumentFile(newFile);
    };

    fileReader.readAsDataURL(fileToLoad);

    fileRef.current.value = "";
  };

  const handleRemoveNewDocumentFile = () => {
    setNewDocumentFile(null);
  };

  const _agendaDocument =
    selectedDocument &&
    selectedDocument.files &&
    selectedDocument.files.length > 0
      ? {
          id: selectedDocument._id,
          option: selectedDocument.files[0].name,
        }
      : "Select Document";
  const handleDisabledSave = () => {
    return (
      !selectedOptionMeeting || !tempAgenda.title || !selectedOptionItemOwner
    );
  };
  return (
    <Modal
      isOpen={isOpen}
      className={styles.modal}
      children={
        <>
          <div className={styles["button-bar"]}>
            {!itemInfo.isActionItem && itemInfo.id ? (
              <ModalActionBar
                children={
                  <>
                    <button
                      type="button"
                      onClick={handleUnpublishAgenda}
                      className={
                        itemInfo.isDraft
                          ? styles.unpublishedButton
                          : styles.activeButton
                      }
                      disabled={itemInfo.isDraft}
                    >
                      Unpublished
                    </button>
                    <button
                      type="button"
                      onClick={handlePublishAgenda}
                      className={
                        !itemInfo.isDraft
                          ? styles.publishedButton
                          : styles.activeButton
                      }
                      disabled={!itemInfo.isDraft}
                    >
                      Published
                    </button>
                  </>
                }
              />
            ) : (
              <div></div>
            )}
            <ModalActionBar
              children={
                <>
                  <button type="button" onClick={onClose}>
                    <CancelButtonImg />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave("save")}
                    disabled={handleDisabledSave()}
                  >
                    <SaveButtonImg />
                  </button>
                  {!!!itemInfo.id ? (
                    <button
                      type="button"
                      onClick={() => handleSave("saveDraft")}
                      disabled={handleDisabledSave()}
                    >
                      <SaveDraftButtonImg />
                    </button>
                  ) : (
                    <></>
                  )}
                </>
              }
            />
          </div>
          <ModalCard
            children={
              <div className={styles.card}>
                <div className={styles["half-flex"]}>
                  <div>
                    <label
                      htmlFor="meeting-select"
                      className={styles["select-label"]}
                    >
                      {tempAgenda.isSubmitted ? "Proposed" : ""} Meeting Date
                      <sup>*</sup>
                    </label>
                    <div className={styles.selectContainer}>
                      {meetingDates.length > 0 ? (
                        <ObjectSelect
                          options={meetingDates}
                          onChange={(e) => {
                            setTempAgenda({
                              ...tempAgenda,
                              meetingId: e,
                            });
                            meetingDates.sort((a, b) => {
                              return new Date(a) - new Date(b);
                            });
                          }}
                          placeholder="Select Date"
                          selectedOption={selectedOptionMeeting}
                        />
                      ) : (
                        selectedOptionMeeting
                      )}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="item-owner"
                      className={styles["select-label"]}
                    >
                      Item Owner<sup>*</sup>
                    </label>
                    <span className={styles.owners}>
                      {itemOwners && itemOwners.length > 0 ? (
                        <ObjectSelect
                          isError={errors && errors.errorItemOwner != ""}
                          options={_itemOwners}
                          onChange={(e) => handleChangeItemOwnerSelected(e)}
                          placeholder="Select Item Owner"
                          selectedOption={selectedOptionItemOwner}
                        />
                      ) : (
                        <div>{selectedOptionItemOwner}</div>
                      )}
                    </span>
                  </div>
                  <div>
                    <label htmlFor="title">
                      Title<sup>*</sup>
                    </label>
                    <Input
                      placeholder="Title"
                      name="title"
                      className={styles["text-input"]}
                      value={tempAgenda.title}
                      onChange={(key) =>
                        setTempAgenda({
                          ...tempAgenda,
                          title: key.target.value,
                        })
                      }
                      ref={titleInput}
                    />
                  </div>
                  <div>
                    <label>Description</label>
                    <TextEditor
                      text={tempDescription}
                      setText={setTempDescription}
                    />
                  </div>
                  <div className={styles["note-editor"]}>
                    <label>Notes</label>
                    <TextEditor text={tempNote} setText={setTempNote} />
                  </div>
                  <Button
                    className={classNames(
                      styles["add-button"],
                      styles["note-button"]
                    )}
                    onClick={() => {
                      if (tempNote !== "") {
                        setTempAgenda({
                          ...tempAgenda,
                          notes: [
                            {
                              author: currentUser.name,
                              date: new Date().toISOString(),
                              content: tempNote,
                              username: currentUser.username,
                              location: "AGENDAS",
                              isNew: true,
                            },
                            ...tempAgenda.notes,
                          ],
                        });
                        setTempNote("");
                        noteRef.current.scroll({ top: 0, behavior: "smooth" });
                      }
                    }}
                    children={<p>Add Note</p>}
                  />

                  <div>
                    <div ref={noteRef} className={styles["note-container"]}>
                      {tempAgenda.notes.length ? (
                        <TransitionGroup component="ul">
                          {tempAgenda.notes.map((note, index) => {
                            let canView = hasPermission(
                              "management-team.admins"
                            );
                            if (
                              canView &&
                              note.location &&
                              note.location.toUpperCase() == "AGENDAS"
                            )
                              return (
                                <CSSTransition
                                  timeout={300}
                                  classNames={{
                                    enterActive: styles["fade-enter-active"],
                                    enter: styles["fade-enter"],
                                    exit: styles["fade-exit"],
                                    exitActive: styles["fade-exit-active"],
                                  }}
                                  key={tempAgenda.notes.length - index}
                                >
                                  <li key={index}>
                                    <div className={styles["note-body"]}>
                                      <p className={styles["note-author"]}>
                                        {note.author}
                                      </p>
                                      <p className={styles["note-date"]}>
                                        {moment(note.date)
                                          .format("MMMM DD, YYYY h:mm a")
                                          .replace("am", "a.m.")
                                          .replace("pm", "p.m.") + " (PT)"}
                                      </p>
                                      <p className={styles["note-content"]}>
                                        <Markdown
                                          content={
                                            note.content ? note.content : ""
                                          }
                                        />
                                      </p>
                                    </div>
                                  </li>
                                </CSSTransition>
                              );
                          })}
                        </TransitionGroup>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles["half-flex"]}>
                  <div>
                    <fieldset className={styles.fieldset}>
                      <legend className={styles["horizontal-line"]}>
                        Add To
                      </legend>
                      <div className={styles["fieldset-set"]}>
                        {["Agenda", "Decisions (Voting)"].map(
                          (addChoice, i) => (
                            <CheckboxItem
                              key={i}
                              label={addChoice}
                              checked={tempAgenda.addTo.includes(addChoice)}
                              onChange={() => {
                                setTempAgenda({
                                  ...tempAgenda,
                                  addTo: tempAgenda.addTo.includes(addChoice)
                                    ? tempAgenda.addTo.filter(
                                        (choice) => choice !== addChoice
                                      )
                                    : [...tempAgenda.addTo, addChoice],
                                  votingRequirements:
                                    !tempAgenda.addTo.includes(
                                      "Decisions (Voting)"
                                    ) && addChoice === "Decisions (Voting)"
                                      ? ['"A Block" Vote']
                                      : tempAgenda.votingRequirements,
                                });
                              }}
                            />
                          )
                        )}
                      </div>
                    </fieldset>
                    <div
                      className={classNames({
                        [styles.hidden]:
                          !tempAgenda.addTo.includes("Decisions (Voting)"),
                      })}
                    >
                      <div className={styles["voting-fieldset"]}>
                        <fieldset className={styles.fieldset}>
                          <legend className={styles["horizontal-line"]}>
                            Voting Requirements
                          </legend>
                          <div className={styles["fieldset-set"]}>
                            {[
                              '"A Block" Vote',
                              '"B Block" Vote',
                              "Multiple Choice",
                            ].map((requirement, i) => (
                              <CheckboxItem
                                key={i}
                                label={requirement}
                                checked={tempAgenda.votingRequirements.includes(
                                  requirement
                                )}
                                onChange={() => {
                                  if (requirement === "Multiple Choice") {
                                    setTempAgenda({
                                      ...tempAgenda,
                                      votingRequirements:
                                        tempAgenda.votingRequirements.includes(
                                          requirement
                                        )
                                          ? ['"A Block" Vote']
                                          : ["Multiple Choice"],
                                    });
                                  } else {
                                    if (requirement === '"B Block" Vote') {
                                      setTempAgenda({
                                        ...tempAgenda,
                                        votingRequirements:
                                          tempAgenda.votingRequirements.includes(
                                            requirement
                                          )
                                            ? tempAgenda.votingRequirements.filter(
                                                (choice) =>
                                                  choice !== requirement
                                              )
                                            : ['"A Block" Vote', requirement],
                                      });
                                    } else if (
                                      requirement === '"A Block" Vote'
                                    ) {
                                      setTempAgenda({
                                        ...tempAgenda,
                                        addTo:
                                          tempAgenda.votingRequirements.includes(
                                            requirement
                                          )
                                            ? tempAgenda.addTo.filter(
                                                (i) =>
                                                  i !== "Decisions (Voting)"
                                              )
                                            : tempAgenda.addTo,
                                        votingRequirements:
                                          tempAgenda.votingRequirements.includes(
                                            requirement
                                          )
                                            ? []
                                            : ['"A Block" Vote'],
                                      });
                                    }
                                  }
                                }}
                              />
                            ))}
                          </div>
                        </fieldset>
                      </div>
                      <div>
                        {tempAgenda.votingRequirements.includes(
                          "Multiple Choice"
                        ) && (
                          <div
                            className={classNames(styles.flex, styles.options)}
                          >
                            <div className={styles["horizontal-line"]}>
                              Add Multiple Choice Options
                            </div>
                            <div className={styles.toggler}>
                              <Toggler
                                key={id}
                                toggled={tempAgenda.allowMultiple}
                                color="#39B54A"
                                onChange={() => {
                                  const newSelections = tempSelections.map(
                                    (_selection) => {
                                      return {
                                        ..._selection,
                                        deleted: tempAgenda.allowMultiple,
                                      };
                                    }
                                  );
                                  setTempAgenda({
                                    ...tempAgenda,
                                    allowMultiple: !tempAgenda.allowMultiple,
                                    selections: !tempAgenda.allowMultiple
                                      ? []
                                      : tempAgenda.selections,
                                  });
                                  setTempSelections(newSelections);
                                }}
                              />
                              <div className={styles["toggler-text"]}>
                                Allow Multiple Selections
                              </div>
                              {tempAgenda.allowMultiple && (
                                <div
                                  style={{
                                    fontSize: "16px",
                                    marginLeft: "10px",
                                  }}
                                >
                                  <OptionsDropdown
                                    options={[1, 2, 3]}
                                    placeholder={
                                      tempAgenda?.maximumChoices || 1
                                    }
                                    openedPlaceholder={"Select"}
                                    onChange={(option) => {
                                      setTempAgenda({
                                        ...tempAgenda,
                                        maximumChoices: option,
                                      });
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <ul>
                              {tempSelections
                                .filter((selection) => !selection.deleted)
                                .map((selection, index) => {
                                  return (
                                    <li
                                      className={styles["selections-li"]}
                                      key={selection.id}
                                    >
                                      <div>
                                        <MultipleChoiceItem
                                          key={selection.id}
                                          itemTitle={selection.selection}
                                          itemPosition={alphabet[index]}
                                          onClick={() => {}}
                                          isEdit={true}
                                          onEdit={(key) => {
                                            const newSelections =
                                              tempSelections.map(
                                                (_selection) => {
                                                  return _selection.id ===
                                                    selection.id
                                                    ? {
                                                        ...selection,
                                                        selection:
                                                          key.target.value,
                                                      }
                                                    : _selection;
                                                }
                                              );
                                            setTempSelections(newSelections);
                                          }}
                                        />
                                      </div>
                                      <button
                                        className={styles["close-selection"]}
                                        onClick={() => {
                                          setTempAgenda({
                                            ...tempAgenda,
                                            selections:
                                              tempAgenda.selections.filter(
                                                (_, arrayIndex) =>
                                                  arrayIndex !== index
                                              ),
                                          });
                                          const newSelections =
                                            tempSelections.map((_selection) => {
                                              if (
                                                _selection.id === selection.id
                                              ) {
                                                return {
                                                  ..._selection,
                                                  deleted: true,
                                                };
                                              }

                                              return _selection;
                                            });
                                          console.log(newSelections);
                                          setTempSelections(newSelections);
                                        }}
                                      >
                                        <img
                                          src={CloseButton}
                                          alt="Close button"
                                        />
                                      </button>
                                    </li>
                                  );
                                })}
                            </ul>
                            <Button
                              className={styles["add-button"]}
                              onClick={() => {
                                if (tempAgenda.selections.length < 10) {
                                  setTempAgenda({
                                    ...tempAgenda,
                                    selections: [
                                      "Choice",
                                      ...tempAgenda.selections,
                                    ],
                                  });
                                  setTempSelections([
                                    {
                                      id: timeId(),
                                      selection: "Choice",
                                      deleted: false,
                                    },
                                    ...tempSelections,
                                  ]);
                                }
                              }}
                              children={<p>Add Choice</p>}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div
                        className={classNames(
                          styles["horizontal-line"],
                          styles.documents
                        )}
                      >
                        Documents
                      </div>
                      {!newDocumentFile ? (
                        <div className={styles.selectContainer}>
                          <ObjectSelect
                            className={styles.select}
                            placeholder="Select document"
                            selectedOption={_agendaDocument}
                            options={documentOptions}
                            onChange={(optionId) =>
                              handleClickDocumentOption(optionId)
                            }
                          />
                        </div>
                      ) : (
                        <ul className={styles["documents-list"]}>
                          <li className={styles["pdf-li"]}>
                            <img src={PDFIcon} alt="" />
                            <p>{newDocumentFile.name || ""}</p>
                            <button
                              type="button"
                              className={styles["remove-button"]}
                              onClick={() => handleRemoveNewDocumentFile()}
                            >
                              <RemoveButtonImg />
                            </button>
                          </li>
                        </ul>
                      )}
                      <label htmlFor="pdf-input" hidden>
                        PDF Document
                      </label>
                      <input
                        hidden
                        ref={fileRef}
                        type="file"
                        name="pdf-input"
                        accept="application/pdf"
                        onChange={(e) => handleNewDocumentFileChange(e)}
                      />
                      {isDocumentDownloadable_Nullable !== null && (
                        <div>
                          <div className={styles.documentsDownload}>
                            <span>Document is Downloadable</span>
                            <div className={styles.documentsDownloadToggle}>
                              <Toggler
                                key={id}
                                visible={true}
                                onChange={() =>
                                  setIsDocumentDownloadable_Nullable(
                                    !isDocumentDownloadable_Nullable
                                  )
                                }
                                toggled={isDocumentDownloadable_Nullable}
                                color={
                                  isDocumentDownloadable_Nullable
                                    ? "#39B54A"
                                    : "#D3D3D3"
                                }
                              />
                            </div>
                          </div>
                          <div className={styles["documents-line"]} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </>
      }
    />
  );
});

const timeId = () => new Date().getTime();

const currentTime = () => {
  const current = new Date();
  return `${current.toLocaleDateString("en-us", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })} ${current
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
    .replace("AM", "a.m.")
    .replace("PM", "p.m.")}`;
};

AddNewModal.propTypes = {
  /**
   * Boolean that determines if the modal is displayed.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * Function that fires when the save button is clicked.
   */
  onSave: PropTypes.func,
  /**
   * Function that fires when the save draft button is clicked.
   */
  onSaveDraft: PropTypes.func,
  /**
   * Object with information to pre-fill the form if the user is editing.
   */
  itemInfo: PropTypes.shape({
    meetingDate: PropTypes.string,
    itemOwner: PropTypes.object,
    title: PropTypes.string,
    description: PropTypes.string,
    notes: PropTypes.arrayOf(
      PropTypes.shape({
        author: PropTypes.string,
        date: PropTypes.string,
        content: PropTypes.string,
      })
    ),
    addTo: PropTypes.arrayOf(PropTypes.oneOf(["Agenda", "Decisions (Voting)"])),
    votingRequirements: PropTypes.arrayOf(
      PropTypes.oneOf(["Requires, Quorum", '"A Block" Vote', '"B Block" Vote'])
    ),
    allowMultiple: PropTypes.bool,
    selections: PropTypes.arrayOf(PropTypes.string),
    documents: PropTypes.array,
  }),
  /**
   * Array of options for the meeting date dropdown.
   */
  meetingDates: PropTypes.arrayOf(PropTypes.object),
  /**
   * Array of options for the item owner dropdown.
   */
  itemOwners: PropTypes.arrayOf(PropTypes.object),
  /**
   * Current User
   */
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
  }),
};

AddNewModal.defaultProps = {
  itemInfo: {
    meetingDate: "",
    itemOwner: {},
    title: "",
    description: "",
    notes: [],
    addTo: ["Agenda"],
    votingRequirements: [],
    allowMultiple: false,
    selections: [],
    documents: [],
  },
};
